import type { Filters } from '@/interfaces';
import { stringify, type ParsedUrlQuery } from 'querystring';
import { safeFormatClientDate } from 'shared/util/datetime-fns';
import { queryParamToString } from 'shared/util/query';
import {
    booleanToQueryParam,
    checkboxMapToQueryParams,
    coordinateToQueryParam,
    escapeQueryParams,
    openNumberRangeToQueryParam,
    pageToQueryParam,
    queryParamsToOpenNumberRange,
    queryParamToCheckboxMap,
    queryParamToDaysAheadEnd,
    queryParamToDaysAheadStart,
    queryParamToDate,
    queryParamsToMapBounds,
    queryParamToInteger,
    queryParamToRooms,
    queryParamToSortOption,
    removeEmptyValues,
    roomsToQueryParams,
    sortDirectionToQueryParam,
    sortToQueryParam,
    queryParamToDayOfWeekStart,
    queryParamToDayOfWeekEnd,
} from './queryParamConverters';
import { replaceDashes, replacePipes } from './queryParamHelpers';
import { adjustInvalidDates } from './invalidDates';

const queryParamsToFilters = (query: ParsedUrlQuery): Partial<Filters> =>
    removeEmptyValues({
        destination: queryParamToString(query.destination),
        checkInDate:
            queryParamToDate(query.checkInDate) ??
            queryParamToDaysAheadStart(query.daysAhead) ??
            queryParamToDayOfWeekStart(query.dayOfWeek),
        checkOutDate:
            queryParamToDate(query.checkOutDate) ??
            queryParamToDaysAheadEnd(query.daysAhead, query.nights) ??
            queryParamToDayOfWeekEnd(query.dayOfWeek, query.nights),
        rooms: queryParamToRooms(query.rooms),
        mapBounds: queryParamsToMapBounds(query.lngSw, query.latSw, query.lngNe, query.latNe),
        page: queryParamToInteger(query.page),
        facility: queryParamToCheckboxMap(query.feature),
        guestRating: queryParamToCheckboxMap(query.guestRating),
        starRating: queryParamToCheckboxMap(query.starRating),
        propertyType: queryParamToCheckboxMap(replaceDashes(query.propertyType)),
        hotelChain: queryParamToCheckboxMap(query.hotelChain),
        sort: queryParamToSortOption(query.sort, query.sortDirection),
        price: queryParamsToOpenNumberRange(query.price),
        hotelName: queryParamToString(query.hotelName),
        utm_source: queryParamToString(query.utm_source),
        utm_medium: queryParamToString(query.utm_medium),
        utm_campaign: queryParamToString(query.utm_campaign),
        utm_term: queryParamToString(query.utm_term),
        utm_content: queryParamToString(query.utm_content),
    });

export const browserQueryParamsToResultPageFilters = (query: ParsedUrlQuery): Partial<Filters> =>
    adjustInvalidDates(queryParamsToFilters(escapeQueryParams(query)));

export const filtersToResultPageBrowserQueryParams = (filters: Partial<Filters>, mapOpen: boolean, keepTracking = false): string =>
    stringify(
        removeEmptyValues({
            checkInDate: safeFormatClientDate(filters.checkInDate, undefined),
            checkOutDate: safeFormatClientDate(filters.checkOutDate, undefined),
            rooms: roomsToQueryParams(filters.rooms),
            lngSw: coordinateToQueryParam(filters.mapBounds?.sw, 0),
            latSw: coordinateToQueryParam(filters.mapBounds?.sw, 1),
            lngNe: coordinateToQueryParam(filters.mapBounds?.ne, 0),
            latNe: coordinateToQueryParam(filters.mapBounds?.ne, 1),
            page: pageToQueryParam(filters.page),
            feature: checkboxMapToQueryParams(filters.facility),
            guestRating: checkboxMapToQueryParams(filters.guestRating),
            starRating: checkboxMapToQueryParams(filters.starRating),
            propertyType: replacePipes(checkboxMapToQueryParams(filters.propertyType)),
            hotelChain: checkboxMapToQueryParams(filters.hotelChain),
            sort: sortToQueryParam(filters.sort),
            sortDirection: sortDirectionToQueryParam(filters.sort),
            price: openNumberRangeToQueryParam(filters.price),
            hotelName: filters.hotelName,
            mapOpen: booleanToQueryParam(mapOpen),
            utm_source: keepTracking && filters.utm_source,
            utm_medium: keepTracking && filters.utm_medium,
            utm_campaign: keepTracking && filters.utm_campaign,
            utm_term: keepTracking && filters.utm_term,
            utm_content: keepTracking && filters.utm_content,
        }),
    );
